import { ActionReducerMapBuilder, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AppDispatch, StoreType } from 'store';
import { parseConsentQuery } from './services/referralUrlQuery';
import { ActionResult, ConsentReferralUrlParams, Status } from './types';
import { updateConsent } from '../../services/api';
import {
  CONSENT_SEND_CONSENT_FROM_LINK_PREFIX,
  CONSENT_UPDATE_CONSENT_FROM_LINK_PREFIX,
  CONSENT_SLICE_NAME,
} from './constants';

interface ThunkApiConfig {
  state: StoreType;
  dispatch: AppDispatch;
  rejectValue: string | null;
}

interface ConsentState {
  urlQueryParams: ConsentReferralUrlParams | undefined;
  status: Status;
  actionResult: ActionResult | undefined;
}

const initialState: ConsentState = {
  urlQueryParams: undefined,
  status: {
    status: 'ok',
  },
  actionResult: undefined,
};

export const sendConsentFromLink = createAsyncThunk<void, void, ThunkApiConfig>(
  CONSENT_SEND_CONSENT_FROM_LINK_PREFIX,
  async (_, { getState, rejectWithValue }) => {
    const { urlQueryParams } = getState().consent;

    if (urlQueryParams == null) return rejectWithValue(null);

    const response = await updateConsent(urlQueryParams);

    if (response.downForMaintenanceMessage != null) {
      return rejectWithValue(response.downForMaintenanceMessage);
    }
  },
  {
    condition: (arg: void, { getState }): boolean => getState().consent.urlQueryParams != null,
  },
);

export const updateConsentFromLink = createAsyncThunk<void, string, ThunkApiConfig>(
  CONSENT_UPDATE_CONSENT_FROM_LINK_PREFIX,
  async (_, { dispatch }) => {
    await dispatch(sendConsentFromLink());
  },
);

const consentSlice = createSlice({
  name: CONSENT_SLICE_NAME,
  initialState: initialState,
  reducers: {
    empty: () => {},
  },
  extraReducers: (builder: ActionReducerMapBuilder<ConsentState>) => {
    builder.addCase(updateConsentFromLink.pending, (state, { meta }) => {
      const consentReferralUrlParams = parseConsentQuery(meta.arg);

      if (consentReferralUrlParams.hid) {
        state.urlQueryParams = {
          ...consentReferralUrlParams,
          hid: consentReferralUrlParams.hid!,
        };
      } else {
        state.actionResult = {
          status: 'error',
        };
      }
    });

    builder.addCase(sendConsentFromLink.fulfilled, (state) => {
      state.actionResult = {
        status: 'success',
        action: state.urlQueryParams?.action,
      };
    });

    builder.addCase(sendConsentFromLink.rejected, (state, action) => {
      if (action.payload != null) {
        state.status = {
          status: 'down-for-maintenance',
          message: action.payload,
        };
      } else {
        state.actionResult = {
          status: 'error',
        };
      }
    });
  },
});

export default consentSlice.reducer;
